import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, Ruler, ShoppingBag, Zap } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SizeChart } from "../components/SizeChart";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { api, money, pushRecent, getRecent, useProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useShop } from "../context/ShopContext";

export default function ProductDetail() {
  const { handle } = useParams();
  const { connected } = useShop();
  const { addItem, busy } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [sizeChartOpen, setSizeChartOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setProduct(null);
    setActiveImg(0);
    api
      .get(`/products/${handle}`)
      .then((r) => {
        if (!r.data.product) setNotFound(true);
        else setProduct(r.data.product);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [handle]);

  useEffect(() => {
    if (!product) return;
    const v =
      product.variants.find((v) => v.availableForSale) || product.variants[0];
    if (v)
      setSelected(
        Object.fromEntries(v.selectedOptions.map((o) => [o.name, o.value]))
      );
    pushRecent(product);
  }, [product]);

  const variant = useMemo(() => {
    if (!product) return null;
    return (
      product.variants.find((v) =>
        v.selectedOptions.every((o) => selected[o.name] === o.value)
      ) || null
    );
  }, [product, selected]);

  const images = product?.images?.length
    ? product.images
    : product?.featuredImage
      ? [product.featuredImage]
      : [];

  useEffect(() => {
    if (variant?.image?.url) {
      const idx = images.findIndex((i) => i.url === variant.image.url);
      if (idx >= 0) setActiveImg(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant?.id]);

  const related = useProducts({ first: 5, q: product?.productType || "__none__" });
  const relatedProducts = related.products.filter((p) => p.handle !== handle).slice(0, 4);
  const recent = getRecent().filter((r) => r.handle !== handle).slice(0, 4);

  const buyNow = async () => {
    if (!variant) return;
    const c = await addItem(variant.id, qty, { silent: true });
    const url = c?.checkoutUrl;
    if (url) window.location.href = url;
  };

  const price = variant?.price || product?.priceRange?.minVariantPrice;
  const compare = variant?.compareAtPrice;
  const onSale =
    compare && price && parseFloat(compare.amount) > parseFloat(price.amount);

  if (loading)
    return (
      <div className="grid gap-8 px-5 pb-20 pt-28 sm:px-10 lg:grid-cols-2 lg:px-16 lg:pt-36">
        <div className="aspect-[4/5] animate-pulse bg-secondary" />
        <div className="space-y-4">
          <div className="h-10 w-3/4 animate-pulse bg-secondary" />
          <div className="h-6 w-1/4 animate-pulse bg-secondary" />
          <div className="h-40 animate-pulse bg-secondary" />
        </div>
      </div>
    );

  if (notFound || !product)
    return (
      <div className="px-5 py-40 text-center sm:px-10" data-testid="product-not-found">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-signal">404</p>
        <h1 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight">
          {connected ? "Product not found" : "Connect Shopify to view products"}
        </h1>
        <Link
          to="/shop"
          className="mt-8 inline-block border border-foreground px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-foreground hover:text-background"
        >
          Back to shop
        </Link>
      </div>
    );

  return (
    <div className="pb-24 lg:pb-0">
      <div className="grid gap-10 px-5 pb-16 pt-24 sm:px-10 lg:grid-cols-2 lg:px-16 lg:pt-32">
        {/* GALLERY */}
        <div data-testid="product-gallery">
          <div className="relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
            <AnimatePresence mode="wait">
              <motion.img
                key={images[activeImg]?.url || "none"}
                src={images[activeImg]?.url}
                alt={images[activeImg]?.altText || product.title}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="h-full w-full object-cover"
              />
            </AnimatePresence>
            {onSale && (
              <span className="absolute left-4 top-4 bg-signal px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                Sale
              </span>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-3">
              {images.map((img, i) => (
                <button
                  key={img.url || i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`aspect-square overflow-hidden border transition-colors ${
                    i === activeImg ? "border-signal" : "border-border hover:border-foreground/50"
                  }`}
                >
                  <img src={img.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div data-testid="product-info">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal">
            {product.productType || "TRAILNONE"}
          </p>
          <h1 className="mt-3 font-display text-3xl font-bold uppercase leading-[0.95] tracking-tighter sm:text-4xl lg:text-5xl">
            {product.title}
          </h1>
          <div className="mt-4 flex items-baseline gap-3 font-mono text-xl">
            {onSale && (
              <span className="text-sm text-muted-foreground line-through">
                {money(compare)}
              </span>
            )}
            <span className={onSale ? "text-signal" : ""} data-testid="product-price">
              {money(price)}
            </span>
          </div>

          {/* OPTIONS */}
          <div className="mt-8 space-y-6">
            {product.options
              .filter((o) => o.name !== "Title")
              .map((opt) => (
                <div key={opt.name}>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      {opt.name}
                      {selected[opt.name] && (
                        <span className="ml-2 text-foreground">{selected[opt.name]}</span>
                      )}
                    </p>
                    {/size/i.test(opt.name) && (
                      <button
                        onClick={() => setSizeChartOpen(true)}
                        data-testid="size-chart-button"
                        className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-signal"
                      >
                        <Ruler className="h-3.5 w-3.5" /> Size chart
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {opt.values.map((val) => {
                      const isSel = selected[opt.name] === val;
                      return (
                        <button
                          key={val}
                          onClick={() => setSelected((s) => ({ ...s, [opt.name]: val }))}
                          data-testid={`option-${opt.name.toLowerCase()}-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`border px-4 py-2.5 font-mono text-xs uppercase tracking-[0.12em] transition-colors ${
                            isSel
                              ? "border-signal bg-signal text-white"
                              : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                          }`}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>

          {/* QTY + AVAILABILITY */}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-border">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                data-testid="qty-decrease"
                aria-label="Decrease quantity"
                className="p-3 text-muted-foreground transition-colors hover:text-foreground"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-10 text-center font-mono text-sm" data-testid="qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty((q) => q + 1)}
                data-testid="qty-increase"
                aria-label="Increase quantity"
                className="p-3 text-muted-foreground transition-colors hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="availability-status">
              {!variant ? (
                <span className="text-muted-foreground">Select options</span>
              ) : variant.availableForSale ? (
                <span className="text-signal">In stock</span>
              ) : (
                <span className="text-muted-foreground">Sold out</span>
              )}
            </p>
          </div>

          {/* ACTIONS */}
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <button
              onClick={() => variant && addItem(variant.id, qty)}
              disabled={!variant?.availableForSale || busy}
              data-testid="add-to-cart-button"
              className="flex items-center justify-center gap-2 border border-foreground py-4 font-mono text-xs font-bold uppercase tracking-[0.22em] transition-colors hover:bg-foreground hover:text-background disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ShoppingBag className="h-4 w-4" />
              Add to cart
            </button>
            <button
              onClick={buyNow}
              disabled={!variant?.availableForSale || busy}
              data-testid="buy-now-button"
              className="flex items-center justify-center gap-2 bg-signal py-4 font-mono text-xs font-bold uppercase tracking-[0.22em] text-white transition-colors hover:bg-signal/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Zap className="h-4 w-4" />
              Buy now
            </button>
          </div>

          {/* ACCORDION */}
          <Accordion type="single" collapsible className="mt-10" defaultValue="description">
            <AccordionItem value="description" data-testid="accordion-description">
              <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.2em]">
                Description
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                {product.description || "Details coming from your Shopify product description."}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="materials" data-testid="accordion-materials">
              <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.2em]">
                Materials & care
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                Heavyweight premium fabric, pre-shrunk and garment-washed. Machine
                wash cold, inside out. Hang dry. Do not iron print. Full fabric
                breakdown lives in the product description managed in Shopify.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping" data-testid="accordion-shipping">
              <AccordionTrigger className="font-mono text-xs uppercase tracking-[0.2em]">
                Shipping & returns
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                Free shipping on orders over $150. Orders ship within 2 business
                days. 30-day returns on unworn gear with tags attached. See{" "}
                <Link to="/shipping-returns" className="text-signal underline underline-offset-4">
                  Shipping & Returns
                </Link>
                .
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      {/* RELATED */}
      {relatedProducts.length > 0 && (
        <section className="border-t border-border px-5 py-16 sm:px-10 lg:px-16" data-testid="related-products">
          <Reveal>
            <h2 className="mb-8 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
              Complete the kit
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {relatedProducts.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* RECENTLY VIEWED */}
      {recent.length > 0 && (
        <section className="border-t border-border px-5 py-16 sm:px-10 lg:px-16" data-testid="recently-viewed">
          <Reveal>
            <h2 className="mb-8 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
              Recently viewed
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {recent.map((r) => (
              <Link
                key={r.handle}
                to={`/product/${r.handle}`}
                data-testid={`recent-${r.handle}`}
                className="group block border border-border bg-card transition-colors hover:border-signal/60"
              >
                <div className="aspect-[4/5] overflow-hidden bg-secondary">
                  {r.image && (
                    <img
                      src={r.image}
                      alt={r.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="flex items-start justify-between gap-2 p-4">
                  <h3 className="truncate font-display text-sm font-semibold uppercase">{r.title}</h3>
                  <span className="font-mono text-sm">{money(r.price)}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* STICKY MOBILE BAR */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t border-border bg-background/90 px-5 py-3 backdrop-blur-xl lg:hidden">
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold uppercase">{product.title}</p>
          <p className="font-mono text-xs text-muted-foreground">{money(price)}</p>
        </div>
        <button
          onClick={() => variant && addItem(variant.id, qty)}
          disabled={!variant?.availableForSale || busy}
          data-testid="sticky-add-to-cart"
          className="shrink-0 bg-signal px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white disabled:opacity-40"
        >
          Add to cart
        </button>
      </div>

      <SizeChart open={sizeChartOpen} onOpenChange={setSizeChartOpen} />
    </div>
  );
}
